/**
 * Schedule Component
 *
 * Booking schedule grid showing bookings for selected employees/projects.
 * Ported from Vue Schedule.vue
 *
 * Features:
 * - Week view: Employee rows with vertical daily booking cards
 * - Month view: Grid with employees on rows, days on columns
 * - Year view: Grid with employees on rows, months on columns
 * - Loading state and empty placeholder
 * - Color legend for booking states
 */

import { Spinner } from "@/components/ui/feedback/Spinner";
import type { BookingCardData } from "@/components/booking/display/BookingCard";
import { useBookings } from "@/hooks/booking/useBookings";
import { useBookingSelection } from "@/hooks/booking/useBookingSelection";
import { useScheduleEntities, useCollapseState } from "@/hooks/schedule";
import type { IServiceEmployee } from "@/services/types";
import { generateCardId } from "@/utils/booking";
import { getVisibleDates } from "@/utils/date";
import classNames from "classnames";
import { panelCollapsedAtom, selectedCardAtom } from "@/store/bookingStore";
import { hoursInDayAtom } from "@/store/userStore";
import { capacityOptionsAtom } from "@/store/capacitySettingsStore";
import { eachDayOfInterval, isSameDay } from "date-fns";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styles from "./Schedule.module.css";
import type { ScheduleProps, WeekGroup, YearMonthData } from "./types";
import { MonthView, WeekView, YearView } from "./views";
import { useAtomValue, useSetAtom } from "jotai";
import { buildWeekGroups } from "./buildWeekGroups";
import { buildYearMonthData } from "./buildYearMonthData";
import { calculateEmployeeStats } from "./calculateEmployeeStats";
import { calculateWeeklySum } from "./calculateWeeklySum";
import EmptyPlaceholder from "./views/EmptyPlaceHolder";

export const Schedule: React.FC<ScheduleProps> = ({
  viewMode,
  employees,
  projects,
  customers,
  startDate,
  endDate,
  hideWeekend,
  isLoading: externalLoading = false,
  onCardClick,
  onBackgroundClick,
  onViewModeChange,
  selectedCardId,
  className,
  "data-testid": testId,
}) => {
  // Fetch bookings using the global filter state (useBookings reads from jotai store)
  const { bookings, bookingCards, isLoading: bookingsLoading } = useBookings();

  // Selection state for edit mode
  const {
    selectMode,
    selectedIds,
    isSelected,
    handleCardClick,
    toggleSelectMode,
    exitSelectMode,
    clearSelection,
    addToSelection,
    removeFromSelection,
    editingEmployeeId,
    enterEmployeeEditMode,
    exitEmployeeEditMode,
    // Range preview for shift-hover (Bug #9023)
    isInRangePreview,
    setRangeHoverTarget,
  } = useBookingSelection();

  const setSelectedCard = useSetAtom(selectedCardAtom);
  const selectedCard = useAtomValue(selectedCardAtom);
  const panelCollapsed = useAtomValue(panelCollapsedAtom);
  const setPanelCollapsed = useSetAtom(panelCollapsedAtom);

  const hoursInDay = useAtomValue(hoursInDayAtom);
  const capacityOptions = useAtomValue(capacityOptionsAtom);
  const isLoading = externalLoading || bookingsLoading;

  // Entity logic extracted to hook
  const { isCustomerMode, activeEntities, customerById } = useScheduleEntities({
    employees,
    customers,
    projectsCount: projects.length,
    bookingCards,
  });

  // Collapse state extracted to hook
  const { collapsedEmployees, toggleEmployeeCollapse } = useCollapseState({
    activeEntities,
  });

  // Selected employee/customer for year view (only shows one at a time)
  const [selectedYearEmployee, setSelectedYearEmployee] = useState<
    number | null
  >(activeEntities.length > 0 ? activeEntities[0].id : null);

  // Handle keyboard shortcut for edit mode
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't trigger when typing in inputs
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement
      ) {
        return;
      }

      // "e" key toggles edit mode
      if (event.key === "e" || event.key === "E") {
        const enteringSelectMode = !selectMode;
        // If entering select mode with a selected card, pre-select it
        if (enteringSelectMode && selectedCard) {
          addToSelection([selectedCard.id]);
          setSelectedCard(null);
        }
        toggleSelectMode();
      }

      // Escape exits edit mode
      if (event.key === "Escape" && selectMode) {
        exitSelectMode();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    selectMode,
    toggleSelectMode,
    exitSelectMode,
    selectedCard,
    addToSelection,
    setSelectedCard,
  ]);

  const previousSelectMode = useRef(selectMode);

  useEffect(() => {
    const wasSelectMode = previousSelectMode.current;

    if (selectMode) {
      if (selectedCard) {
        setSelectedCard(null);
      }
      if (panelCollapsed) {
        setPanelCollapsed(false);
      }
    } else if (wasSelectMode) {
      setPanelCollapsed(false);
    }

    previousSelectMode.current = selectMode;
  }, [
    panelCollapsed,
    selectMode,
    selectedCard,
    setPanelCollapsed,
    setSelectedCard,
  ]);

  // Sync selectedYearEmployee when entities or view change
  useEffect(() => {
    if (activeEntities.length === 0) return;

    if (
      viewMode === "year" &&
      editingEmployeeId !== null &&
      activeEntities.some((entity) => entity.id === editingEmployeeId)
    ) {
      setSelectedYearEmployee(editingEmployeeId);
      return;
    }

    const shouldResetToFirst =
      selectedYearEmployee === null ||
      !activeEntities.some((e) => e.id === selectedYearEmployee);

    if (shouldResetToFirst) {
      setSelectedYearEmployee(activeEntities[0].id);
    }
  }, [activeEntities, selectedYearEmployee, viewMode, editingEmployeeId]);

  // ============ CALLBACKS ============

  // Wrapper for entering per-employee edit mode that preserves the selected card
  const handleEnterEmployeeEditMode = useCallback(
    (employeeId: number) => {
      if (selectedCard) {
        addToSelection([selectedCard.id]);
        setSelectedCard(null);
      }
      enterEmployeeEditMode(employeeId);
    },
    [selectedCard, addToSelection, setSelectedCard, enterEmployeeEditMode],
  );

  // ============ COMPUTED VALUES ============

  // Check if we have any filters selected
  const hasFilters =
    activeEntities.length > 0 || projects.length > 0 || customers.length > 0;

  // All days in the date range (including weekends)
  const allDays = useMemo(
    () => eachDayOfInterval({ start: startDate, end: endDate }),
    [startDate, endDate],
  );

  // Get visible days based on hideWeekend setting (filtered for calculations)
  const visibleDays = useMemo(
    () => getVisibleDates(allDays, hideWeekend),
    [allDays, hideWeekend],
  );

  // Group bookings by employee
  const bookingsByEmployee = useMemo(() => {
    const grouped = new Map<number, typeof bookings>();

    bookings.forEach((booking) => {
      const entityId = isCustomerMode
        ? booking.customer?.id
        : booking.employee?.id;
      if (!entityId) return;
      const existing = grouped.get(entityId) || [];
      grouped.set(entityId, [...existing, booking]);
    });

    return grouped;
  }, [bookings, isCustomerMode]);

  // ============ HELPER FUNCTIONS ============

  // Calculate employee capacity stats
  const getEmployeeStats = useCallback(
    (employee: IServiceEmployee) =>
      calculateEmployeeStats(
        employee,
        bookingsByEmployee,
        visibleDays,
        hoursInDay,
        capacityOptions,
      ),
    [bookingsByEmployee, visibleDays, hoursInDay, capacityOptions],
  );

  // Get bookings for a specific employee and day
  const getBookingsForDay = useCallback(
    (employee: IServiceEmployee, day: Date) => {
      const empBookings = bookingsByEmployee.get(employee.id) || [];
      return empBookings.filter((b) => isSameDay(b.date, day));
    },
    [bookingsByEmployee],
  );

  // Create BookingCardData for a specific employee and day
  const getCardForDay = useCallback(
    (employee: IServiceEmployee, day: Date): BookingCardData => {
      const dayBookings = getBookingsForDay(employee, day);
      const customer = isCustomerMode
        ? (customerById.get(employee.id) ?? null)
        : null;

      return {
        id: isCustomerMode
          ? generateCardId(day, null, customer)
          : generateCardId(day, employee, null),
        date: day,
        employee: isCustomerMode ? null : employee,
        customer,
        bookings: dayBookings,
        excludedBookings: [],
      };
    },
    [customerById, getBookingsForDay, isCustomerMode],
  );

  // ============ MONTH VIEW DATA ============

  // Group days by ISO week (always include weekends for placeholder rendering)
  const weekGroups: WeekGroup[] = useMemo(
    () => buildWeekGroups(allDays),
    [allDays],
  );

  // Calculate weekly sum for an employee
  const getWeeklySum = useCallback(
    (employee: IServiceEmployee, weekStartDate: Date): number =>
      calculateWeeklySum(employee, weekStartDate, bookingsByEmployee, projects),
    [bookingsByEmployee, projects],
  );

  // Days per week for grid layout (5 when weekends hidden, 7 otherwise)
  const daysPerWeek = hideWeekend ? 5 : 7;

  // ============ YEAR VIEW DATA ============

  // Year view needs all days (including weekends) to render placeholders
  const yearMonthData: YearMonthData = useMemo(
    () => buildYearMonthData(allDays, selectedYearEmployee, bookingsByEmployee),
    [allDays, selectedYearEmployee, bookingsByEmployee],
  );

  // ============ RENDER HELPERS ============

  const handleViewDetails = useCallback(
    (card: BookingCardData) => {
      // Toggle off if the user clicks the currently selected card again.
      if (selectedCard?.id === card.id) {
        setSelectedCard(null);
        setPanelCollapsed(false);
        return;
      }

      setSelectedCard(card);
      setPanelCollapsed(false);
      onCardClick?.(card);
    },
    [onCardClick, selectedCard, setPanelCollapsed, setSelectedCard],
  );

  const effectiveSelectedCardId = selectedCardId ?? selectedCard?.id;

  const renderLoading = () => (
    <Spinner size="lg" label="Laster bookinger..." centered />
  );

  // Shared props for view components
  const sharedViewProps = {
    employees: activeEntities,
    visibleDays,
    hideWeekend,
    hoursInDay,
    bookingsByEmployee,
    collapsedEmployees,
    toggleEmployeeCollapse,
    selectMode,
    selectedCardId: effectiveSelectedCardId,
    selectedIds,
    isSelected,
    addToSelection,
    removeFromSelection,
    handleCardClick,
    onCardClick: handleViewDetails,
    toggleSelectMode,
    getEmployeeStats,
    getBookingsForDay,
    getCardForDay,
    onViewModeChange,
    // Range preview for shift-hover (Bug #9023)
    isInRangePreview,
    setRangeHoverTarget,
  };

  // ============ MAIN RENDER ============

  const renderContent = () => {
    if (isLoading) {
      return renderLoading();
    }

    if (!hasFilters || (activeEntities.length === 0 && bookings.length === 0)) {
      return <EmptyPlaceholder hasFilters={hasFilters} />;
    }

    switch (viewMode) {
      case "week":
        return (
          <WeekView
            {...sharedViewProps}
            hideAggregate={isCustomerMode}
            showIndividualEditButtons={activeEntities.length >= 1}
            editingEmployeeId={editingEmployeeId}
            onEnterEmployeeEditMode={handleEnterEmployeeEditMode}
            onExitEmployeeEditMode={exitEmployeeEditMode}
          />
        );
      case "month":
        return (
          <MonthView
            {...sharedViewProps}
            weekGroups={weekGroups}
            daysPerWeek={daysPerWeek}
            getWeeklySum={getWeeklySum}
            hideAggregate={isCustomerMode}
            showIndividualEditButtons={activeEntities.length >= 1}
            editingEmployeeId={editingEmployeeId}
            onEnterEmployeeEditMode={handleEnterEmployeeEditMode}
            onExitEmployeeEditMode={exitEmployeeEditMode}
          />
        );
      case "year":
        return (
          <YearView
            {...sharedViewProps}
            yearMonthData={yearMonthData}
            selectedYearEmployee={selectedYearEmployee}
            setSelectedYearEmployee={setSelectedYearEmployee}
            clearSelection={clearSelection}
            hideAggregate={isCustomerMode}
            showIndividualEditButton={selectedYearEmployee !== null}
            editingEmployeeId={editingEmployeeId}
            onEnterEmployeeEditMode={handleEnterEmployeeEditMode}
            onExitEmployeeEditMode={exitEmployeeEditMode}
          />
        );
      default:
        return <EmptyPlaceholder hasFilters={hasFilters} />;
    }
  };

  // Handle background click (clicking on schedule but not on a card)
  const handleBackgroundClick = useCallback(() => {
    // Don't trigger in select mode
    if (selectMode) {
      return;
    }

    // If a card is selected, deselect it and return to black datepicker panel
    if (selectedCard) {
      setSelectedCard(null);
      onBackgroundClick?.();
    }
  }, [onBackgroundClick, selectMode, selectedCard, setSelectedCard]);

  return (
    <div
      className={classNames(styles.schedule, className)}
      data-testid={testId}
      onClick={handleBackgroundClick}
    >
      {renderContent()}
    </div>
  );
};

Schedule.displayName = "Schedule";

export default Schedule;
